import React from "react";
import styled from "styled-components";
import { colorTransition, focusRing, iconSlot } from "./base/mixins";

export interface TopbarChipProps
	// `value` on a <button> is a string attribute; here it is the chip's displayed
	// value and may be any node, so the DOM one is dropped rather than widened.
	extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "value"> {
	/** 10px secondary micro-label above the value. §2, §8. */
	microLabel: string;
	value: React.ReactNode;
}

/**
 * DESIGN_SYSTEM.md §8's top-bar dropdown chip: 36px tall, 6px radius, 1px border,
 * white, with a 24px chevron.
 */
const TopbarChip = React.forwardRef<HTMLButtonElement, TopbarChipProps>(
	({ microLabel, value, ...rest }, ref) => (
		<Chip ref={ref} type="button" {...rest}>
			<span className="hds-chip__text">
				<span className="hds-chip__micro">{microLabel}</span>
				<span className="hds-chip__value">{value}</span>
			</span>
			<span className="hds-chip__chevron" aria-hidden>
				<svg
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeLinecap="round"
					strokeLinejoin="round"
				>
					<path d="M6 9l6 6 6-6" />
				</svg>
			</span>
		</Chip>
	),
);

TopbarChip.displayName = "TopbarChip";

const Chip = styled.button`
	display: inline-flex;
	align-items: center;
	gap: var(--space-2);
	height: var(--size-control-chip);
	padding: 0 var(--space-3);
	border: var(--border-default);
	border-radius: var(--radius-md);
	background: var(--color-surface);
	cursor: pointer;
	box-shadow: none;
	${colorTransition}
	${focusRing}

	.hds-chip__text {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		min-width: 0;
		text-align: left;
	}

	.hds-chip__micro {
		font-family: var(--font-family);
		font-size: var(--font-size-micro);
		line-height: 1.3;
		color: var(--color-text-secondary);
	}

	.hds-chip__value {
		max-width: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-family: var(--font-family);
		font-size: var(--font-size-body);
		font-weight: var(--font-weight-medium);
		line-height: var(--line-height-base);
		color: var(--color-text);
	}

	.hds-chip__chevron {
		${iconSlot}
		color: var(--color-text);
	}

	&:hover {
		background: var(--color-hover-ground);
		border-color: var(--color-border-strong);
	}
`;

export default TopbarChip;
